// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  BffInboundMessage,
  CoachMarkErrorEvent,
  CoachMarkResetAckEvent,
  CoachMarkShownAckEvent,
  CoachMarkStateEvent,
  CoachMarkStateItem,
  getSharedBffWebSocket,
} from '@/services/bffWebSocket';

const DEFAULT_MAX_SHOWS = 4;
const REQUEST_TIMEOUT_MS = 10_000;
const RETRY_DELAY_MS = 2_000;
const DUPLICATE_MARK_GUARD_MS = 1500;
const shownInFlight = new Set<string>();
const lastMarkedAtByKey = new Map<string, number>();

const toDefaultState = (key: string): CoachMarkStateItem => ({
  key,
  shownCount: 0,
  maxShows: DEFAULT_MAX_SHOWS,
  shouldShow: true,
});

const normalizeState = (state: CoachMarkStateItem): CoachMarkStateItem => {
  const normalizedMaxShows = Math.max(state.maxShows || 0, DEFAULT_MAX_SHOWS);
  const normalizedShownCount = Math.max(0, Math.min(state.shownCount || 0, normalizedMaxShows));
  return {
    key: state.key,
    shownCount: normalizedShownCount,
    maxShows: normalizedMaxShows,
    shouldShow: normalizedShownCount < normalizedMaxShows,
  };
};

const sanitizeKeys = (keys: readonly string[]): string[] =>
  Array.from(
    new Set(
      keys
        .map((key) => key?.trim())
        .filter((key): key is string => !!key && key.length > 0)
    )
  );

const buildRequestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `coach-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
};

type CoachMarkListener = () => void;

class CoachMarkClient {
  private readonly socket = getSharedBffWebSocket(this.apiBaseUrl);
  private readonly cache = new Map<string, CoachMarkStateItem>();
  private readonly listeners = new Set<CoachMarkListener>();

  constructor(private readonly apiBaseUrl: string) {}

  subscribe(listener: CoachMarkListener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getState(key: string): CoachMarkStateItem | null {
    const normalized = key.trim();
    if (!normalized) return null;
    return this.cache.get(normalized) ?? null;
  }

  async refresh(keys: readonly string[]): Promise<void> {
    const normalizedKeys = sanitizeKeys(keys);
    if (normalizedKeys.length === 0) return;

    const requestId = buildRequestId();
    this.socket.sendCoachMarkStateRequest({ requestId, keys: normalizedKeys });
    const response = await this.waitForResponse<CoachMarkStateEvent>(requestId, 'coachMark.state');
    const items = Array.isArray(response.items) ? response.items.map(normalizeState) : [];
    const byKey = new Map(items.map((item) => [item.key, item] as const));
    for (const key of normalizedKeys) {
      this.cache.set(key, byKey.get(key) ?? toDefaultState(key));
    }
    this.notify();
  }

  async markShown(key: string): Promise<void> {
    const normalizedKey = key.trim();
    if (!normalizedKey) return;

    const requestId = buildRequestId();
    this.socket.sendCoachMarkShown({ requestId, key: normalizedKey });
    const response = await this.waitForResponse<CoachMarkShownAckEvent>(requestId, 'coachMark.shown.ack');
    this.cache.set(normalizedKey, normalizeState({
      key: response.key,
      shownCount: response.shownCount,
      maxShows: response.maxShows ?? DEFAULT_MAX_SHOWS,
      shouldShow: response.shouldShow,
    }));
    this.notify();
  }

  async resetAll(): Promise<void> {
    const requestId = buildRequestId();
    this.socket.sendCoachMarkReset(requestId);
    await this.waitForResponse<CoachMarkResetAckEvent>(requestId, 'coachMark.reset.ack');
    for (const [key, state] of this.cache.entries()) {
      this.cache.set(key, {
        key,
        shownCount: 0,
        maxShows: state.maxShows || DEFAULT_MAX_SHOWS,
        shouldShow: true,
      });
    }
    this.notify();
  }

  private notify() {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private waitForResponse<T extends BffInboundMessage>(requestId: string, successType: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      let settled = false;
      let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
      const unsubscribe = this.socket.onMessage((message) => {
        const typed = message as { type?: string; requestId?: string };
        if (typed.requestId !== requestId) return;
        if (typed.type === successType) {
          settled = true;
          cleanup();
          resolve(message as T);
          return;
        }
        if (typed.type === 'coachMark.error') {
          settled = true;
          cleanup();
          const errorMessage =
            (message as CoachMarkErrorEvent).message ??
            (message as CoachMarkErrorEvent).code ??
            'Coach mark request failed.';
          reject(new Error(errorMessage));
        }
      });

      const cleanup = () => {
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
        unsubscribe();
      };

      timeoutHandle = setTimeout(() => {
        if (settled) return;
        cleanup();
        reject(new Error(`Timed out waiting for ${successType}.`));
      }, REQUEST_TIMEOUT_MS);
    });
  }
}

const clients = new Map<string, CoachMarkClient>();

const getCoachMarkClient = (apiBaseUrl: string): CoachMarkClient => {
  const existing = clients.get(apiBaseUrl);
  if (existing) return existing;
  const next = new CoachMarkClient(apiBaseUrl);
  clients.set(apiBaseUrl, next);
  return next;
};

export const tournamentsCreateCoachMarkKey = 'tournaments.createButton';
export const profileImageActionsCoachMarkKey = 'profile.imageActions';
export const holeCompactScoreCoachMarkKey = 'hole.compactScoreButton';
export const knownCoachMarkKeys = [
  tournamentsCreateCoachMarkKey,
  profileImageActionsCoachMarkKey,
  holeCompactScoreCoachMarkKey,
] as const;

type UseCoachMarkResult = {
  visible: boolean;
  shownCount: number;
  maxShows: number;
  loading: boolean;
  markShown: () => Promise<void>;
  refresh: () => Promise<void>;
};

export const useCoachMark = (apiBaseUrl: string, key: string, enabled = true): UseCoachMarkResult => {
  const normalizedKey = key.trim();
  const trackingKey = `${apiBaseUrl}::${normalizedKey}`;
  const client = useMemo(() => getCoachMarkClient(apiBaseUrl), [apiBaseUrl]);
  const [state, setState] = useState<CoachMarkStateItem | null>(() => client.getState(normalizedKey));
  const [loading, setLoading] = useState<boolean>(enabled);
  const [hasError, setHasError] = useState(false);
  const markedForThisMountRef = useRef(false);

  useEffect(() => {
    const unsubscribe = client.subscribe(() => {
      setState(client.getState(normalizedKey));
    });
    return unsubscribe;
  }, [client, normalizedKey]);

  const refresh = useCallback(async () => {
    if (!enabled || !normalizedKey) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setHasError(false);
    try {
      await client.refresh([normalizedKey]);
    } catch (error) {
      console.warn('coach mark state refresh failed', { key: normalizedKey, error });
      setHasError(true);
    } finally {
      setLoading(false);
    }
  }, [client, enabled, normalizedKey]);

  const markShown = useCallback(async () => {
    if (!enabled || !normalizedKey) return;
    if (shownInFlight.has(trackingKey)) return;
    const now = Date.now();
    const lastMarkedAt = lastMarkedAtByKey.get(trackingKey) ?? 0;
    if (now - lastMarkedAt < DUPLICATE_MARK_GUARD_MS) {
      markedForThisMountRef.current = true;
      return;
    }
    shownInFlight.add(trackingKey);
    setHasError(false);
    try {
      await client.markShown(normalizedKey);
      lastMarkedAtByKey.set(trackingKey, now);
      markedForThisMountRef.current = true;
    } catch (error) {
      console.warn('coach mark shown update failed', { key: normalizedKey, error });
      setHasError(true);
      markedForThisMountRef.current = false;
      throw error;
    } finally {
      shownInFlight.delete(trackingKey);
    }
  }, [client, enabled, normalizedKey, trackingKey]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!enabled || !hasError) return;
    const timer = setTimeout(() => {
      void refresh();
    }, RETRY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [enabled, hasError, refresh]);

  useEffect(() => {
    if (!enabled || !state || state.shownCount === 0) {
      markedForThisMountRef.current = false;
    }
  }, [enabled, normalizedKey, state?.shownCount, state]);

  useEffect(() => {
    if (!enabled || !normalizedKey || loading || hasError) return;
    if (!state?.shouldShow) return;
    if (markedForThisMountRef.current) return;

    markedForThisMountRef.current = true;
    void markShown().catch(() => {
      // Error already handled in markShown().
    });
  }, [enabled, hasError, loading, markShown, normalizedKey, state, trackingKey]);

  return {
    visible: enabled && !loading && !hasError && !!state?.shouldShow,
    shownCount: state?.shownCount ?? 0,
    maxShows: state?.maxShows ?? DEFAULT_MAX_SHOWS,
    loading,
    markShown,
    refresh,
  };
};

export const resetCoachMarksForDebug = async (apiBaseUrl: string): Promise<void> => {
  await getCoachMarkClient(apiBaseUrl).resetAll();
  shownInFlight.clear();
  lastMarkedAtByKey.clear();
};

export const getCoachMarkStatesForDebug = async (
  apiBaseUrl: string,
  keys: readonly string[] = knownCoachMarkKeys
): Promise<CoachMarkStateItem[]> => {
  const normalizedKeys = sanitizeKeys(keys);
  if (normalizedKeys.length === 0) return [];

  const client = getCoachMarkClient(apiBaseUrl);
  await client.refresh(normalizedKeys);
  return normalizedKeys.map((key) => client.getState(key) ?? toDefaultState(key));
};
